import React from "react";
import {OVERFLOW_TABLE_WIDTH, PROPERTIES, SUMMIT_ESCROW_TRACKING} from "../../consts/tables";
import Input from "../Input";
import Select from "../Select";
import {Link} from "react-router";
import arrowDown from "../../svgs/arrow_down.svg";
import arrowRight from "../../svgs/arrow_right.svg";
import Alert from "../Alert";
import {dateISOtoUS, dateUStoISO} from "../../date-converter";
import {addCommasToNumber} from "../../utils";
import moment from "moment";
import request from "arcdynamic-request";
import {ESCROW_TRACKING, ESCROW_TRACKING_LIST, HSC_TRACKING, HSC_TRACKING_LIST} from "../../consts/queries";
import {tasks} from "../EscrowTracking/EscrowTracking";
import {executeQuery} from "../../requests/executeQuery";
import {syncPropertyEmailNotifications} from "../../requests/emailRequests";
import SellerDrawer from "../SellerDrawer/SellerDrawer";
import BuyerDrawer from "../BuyerDrawer/BuyerDrawer";
import SelectEscrowOfficer from "../SelectEscrowOfficer/SelectEscrowOfficer";

let cloneDeep = require('lodash.clonedeep');
let findIndex = require('lodash.findindex');

function convertDatesToUS(dateProps, dataToConvert) {
    const data = cloneDeep(dataToConvert);

    Object.keys(data).forEach(row => {
        dateProps.forEach(key => {
            const str = dateISOtoUS(data[row][key]);
            if (str !== false)
                data[row][key] = str;
        });
    });

    return data;
}

function convertDatesToISO(dateProps, dataToConvert) {
    const data = cloneDeep(dataToConvert);

    Object.keys(data).forEach(row => {
        dateProps.forEach(key => {
            const str = dateUStoISO(data[row][key]);
            if (str !== false)
                data[row][key] = str;
        });
    });

    return data;
}

function convertCheckboxValueToNumber(checkboxProps, data) {
    const newData = cloneDeep(data);

    Object.keys(newData).forEach(row => {
        checkboxProps.forEach(key => {
            if (newData[row][key])
                newData[row][key] = parseInt(newData[row][key], 10);
        });
    });

    return newData;
}

function convertCheckboxValueToString(checkboxProps, data) {
    const newData = cloneDeep(data);

    Object.keys(newData).forEach(row => {
        checkboxProps.forEach(key => {
            newData[row][key] = newData[row][key] ? '1' : '0';
        });
    });

    return newData;
}

function convertPrice(priceProps, data) {
    const newData = cloneDeep(data);

    Object.keys(newData).forEach(row => {
        priceProps.forEach(key => {
            if (newData[row][key])
                newData[row][key] = '$' + addCommasToNumber(newData[row][key]);
        });
    });

    return newData;
}

function convertPercentages(percentProps, data) {
    const newData = cloneDeep(data);

    Object.keys(newData).forEach(row => {
        percentProps.forEach(key => {
            if (newData[row][key])
                newData[row][key] = parseFloat(newData[row][key]).toFixed(2) + '%';
        });
    });

    return newData;
}

function getBuyerName(property) {
    if (property.BUYER_INVESTOR_NAME) return property.BUYER_INVESTOR_NAME;
    const parts = [property.BUYER_FIRST_NAME, property.BUYER_LAST_NAME].filter(Boolean);
    return parts.join(' ');
}

function getBuyingAgentName(property) {
    let buyingAgentName = null;

    if (property.AGENT_FIRST_NAME || property.AGENT_LAST_NAME) {
        buyingAgentName = `${property.AGENT_FIRST_NAME} ${property.AGENT_LAST_NAME}`;
    }
    if (property.TEAM_FIRST_NAME || property.TEAM_LAST_NAME) {
        buyingAgentName = `${property.TEAM_FIRST_NAME} ${property.TEAM_LAST_NAME}`;
    }

    return buyingAgentName;
}

function getBuyingAgentFirm(property) {
    // Internal team members have no FIRMS row of their own — BUYER_AGENT_4_FIRM
    // only ever pairs with the external AGENTS slot (BUYER_AGENT_4). Mirrors the
    // source === 'SUMMIT' precedent below for listing agents: hardcoded, no
    // branching by TEAM role family (Peak/HSC show the same "SUMMIT" today).
    if (property.TEAM_FIRST_NAME || property.TEAM_LAST_NAME) {
        return 'SUMMIT';
    }

    return property.FIRM_NAME || null;
}

function getListingAgentName(property) {
    const fromListingAgent1 = `${property.LISTING_AGENT_1_FIRST_NAME || ''} ${property.LISTING_AGENT_1_LAST_NAME || ''}`.trim();
    const fromListingAgent4 = `${property.LISTING_AGENT_4_FIRST_NAME || ''} ${property.LISTING_AGENT_4_LAST_NAME || ''}`.trim();
    // PROPERTIES.SOURCE — Escrow queries must select it.
    // Internal listings (Summit team) use TEAM LISTING_AGENT_1; outside listings use AGENTS LISTING_AGENT_4.
    const source = property.SOURCE;
    if (source === 'SUMMIT') {
        return fromListingAgent1 || null;
    }
    if (fromListingAgent4) {
        return fromListingAgent4;
    }
    return fromListingAgent1 || null;
}

function getTransactionCoordinatorName(property) {
    let transactionCoordinatorName = null;

    if (property.TRANSACTION_COORDINATOR_FIRST_NAME || property.TRANSACTION_COORDINATOR_LAST_NAME) {
        transactionCoordinatorName = `${property.TRANSACTION_COORDINATOR_FIRST_NAME} ${property.TRANSACTION_COORDINATOR_LAST_NAME}`;
    }

    return transactionCoordinatorName;
}

function getSellerName(property) {
    return property.INVESTOR_NAME || '';
}

function getAgentData(data) {
    const newData = cloneDeep(data);

    newData.forEach(row => {
        row.BUYER_NAME = getBuyerName(row);
        row.BUYING_AGENT_NAME = getBuyingAgentName(row);
        row.FIRM_NAME = getBuyingAgentFirm(row);
        row.LISTING_AGENT_NAME = getListingAgentName(row);
        row.SELLER_NAME = getSellerName(row);
        row.TRANSACTION_COORDINATOR_NAME = getTransactionCoordinatorName(row);
    });

    return newData;
}

function getBorrowerName(data) {
    const newData = cloneDeep(data);

    newData.forEach(row => {
        switch (row.LOAN_TYPE) {
            case 'Purchase':
                row.BORROWER_NAME = row.BUYER_NAME ||
                    [row.BUYER_FIRST_NAME, row.BUYER_LAST_NAME].filter(Boolean).join(' ') || '';
                break;
            case 'Construction':
            case 'Refi':
                row.BORROWER_NAME = row.INVESTOR_NAME || '';
                break;
            default:
                row.BORROWER_NAME = '';
        }
        delete row.BUYER_NAME;
        delete row.BUYER_FIRST_NAME;
        delete row.BUYER_LAST_NAME;
        delete row.INVESTOR_NAME;
    });

    return newData;
}

export function convertToTableFormat({dateProps, checkboxProps, priceProps, percentProps, data, hasAgentData = false, hasBorrower = false}) {
    let newData = convertDatesToUS(dateProps, data);
    newData = convertCheckboxValueToNumber(checkboxProps, newData);
    newData = convertPrice(priceProps, newData);
    if (percentProps)
        newData = convertPercentages(percentProps, newData);
    if (hasAgentData)
        newData = getAgentData(newData);
    if (hasBorrower)
        newData = getBorrowerName(newData);
    return newData;
}

export function convertToBackendFormat({dateProps, checkboxProps, data}) {
    let newData = convertDatesToISO(dateProps, data);
    newData = convertCheckboxValueToString(checkboxProps, newData);
    return newData;
}

function calculateDate(effectiveDate, period, extension) {
    let daysToAdd = 0;
    let date = moment(effectiveDate);
    date.add(period + extension, 'days');

    // If weekend, roll to the next business day
    if (date.day() === 0) {  // If day is Sunday
        daysToAdd = 1;
    }
    if (date.day() === 6) {  // If day is Saturday
        daysToAdd = 2;
    }

    if (daysToAdd > 0) {
        date.add(daysToAdd, 'days');
    }

    return date.format('MM/DD/YYYY');
}

function compare(obj1, obj2) {
    const date1 = new Date(obj1.FEASIBILITY_PERIOD_EXPIRES__DEPOSIT_NONREFUNDABLE).getTime() >= new Date().getTime() + 24 * 60 * 60 * 1000 ?
        obj1.FEASIBILITY_PERIOD_EXPIRES__DEPOSIT_NONREFUNDABLE : obj1.CLOSING_DATE;
    const date2 = new Date(obj2.FEASIBILITY_PERIOD_EXPIRES__DEPOSIT_NONREFUNDABLE).getTime() >= new Date().getTime() + 24 * 60 * 60 * 1000 ?
        obj2.FEASIBILITY_PERIOD_EXPIRES__DEPOSIT_NONREFUNDABLE : obj2.CLOSING_DATE;

    if (date1 > date2) {
        return 1;
    } else if (date1 < date2) {
        return -1;
    }
    return 0;
}

// Sort by Feasibility Period and Closing Date
function sort(data) {
    if (!data) return false;
    const sortedData = cloneDeep(data);
    return sortedData.sort(compare);
}

const width = 100 - 14 * 4;
const headerCellHeight = 62;
const cellHeight = 43;
const headerCellHeightSmall = 65;
const cellHeightSmall = 44.5;
const limitWidth = 992;

export default class Tracking extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            data: null,
            original: null,
            isBusy: null,
            error: null,
            success: null,
            width: 1400,
            collapsed: {},
            checkboxes: {
                'NEGOTIATION_CAP_RATE': true,
                'BUYING_COMPANY_STATE': true,
                'EFFECTIVE_DATE': true,
                'FEASIBILITY_PERIOD_EXPIRES__DEPOSIT_NONREFUNDABLE': true,
            },
            areAllCollapsed: false,
            title: '',
            addNewItemType: null,
            addNewItemKey: null,
            newBuyer: null,
            newSeller: null,
            index: null,
        };

        this.handleChange = this.handleChange.bind(this);
        this.handleSave = this.handleSave.bind(this);
        this.handleCollapse = this.handleCollapse.bind(this);
        this.updateWindowDimensions = this.updateWindowDimensions.bind(this);
        this.addNewItem = this.addNewItem.bind(this);
        this.savedNewItem = this.savedNewItem.bind(this);
        this.closeDrawer = this.closeDrawer.bind(this);
    }

    static initCollapsing(tasks) {
        const collapsed = {};
        tasks.map(task => {
            collapsed[task.id] = false;
        });
        return collapsed;
    }

    fetch({propertyId, dateProps, checkboxProps, priceProps, percentProps, title, saved = false, isHsc = false}) {
        const state = {
            data: null,
            original: null,
            error: null,
            success: saved ? 'Saved' : null,
        };

        if (!saved) {
            state.title = title;
            state.collapsed = Tracking.initCollapsing(tasks);
        }

        this.setState(state, () => {
            let queryName;
            const param = {};
            if (propertyId) {
                queryName = isHsc ? HSC_TRACKING : ESCROW_TRACKING;
                param.PROPERTY_ID = propertyId;
            } else {
                queryName = isHsc ? HSC_TRACKING_LIST : ESCROW_TRACKING_LIST;
            }
            this.fetchTrackingData({queryName, param, dateProps, checkboxProps, priceProps, percentProps, isHsc});
        });
    }

    fetchTrackingData({queryName, param, dateProps, checkboxProps, priceProps, percentProps, isHsc}) {
        executeQuery({queryName, params: param}).then(res => {
            const rawData = res && Array.isArray(res.data) ? res.data : [];
            // Dedupe by escrow row id when PROPERTY_CONTRACT_INFO has multiple rows per property (no subquery support in ArcDB).
            // Coalesce PCI/joined fields so a populated value from any duplicate row wins over null.
            const coalesceFields = [
                'TRANSACTION_COORDINATOR',
                'TRANSACTION_COORDINATOR_FIRST_NAME',
                'TRANSACTION_COORDINATOR_LAST_NAME',
                'LISTING_AGENT_1_FIRST_NAME',
                'LISTING_AGENT_1_LAST_NAME',
                'LISTING_AGENT_4_FIRST_NAME',
                'LISTING_AGENT_4_LAST_NAME',
                'INVESTOR_NAME',
                'INVESTOR_LEAD_ID',
            ];
            const deduped = !isHsc
                ? (() => {
                    const map = new Map();
                    const order = [];
                    rawData.forEach(row => {
                        const id = row.ARCIMEDES_ROW_ID;
                        if (!map.has(id)) {
                            map.set(id, {...row});
                            order.push(id);
                        } else {
                            const existing = map.get(id);
                            coalesceFields.forEach(field => {
                                if (!existing[field] && row[field]) existing[field] = row[field];
                            });
                        }
                    });
                    return order.map(id => map.get(id));
                })()
                : rawData;
            const data = isHsc
                ? [...deduped].sort((a, b) => {
                    if (!a.CLOSE_OF_ESCROW) return 1;
                    if (!b.CLOSE_OF_ESCROW) return -1;
                    return a.CLOSE_OF_ESCROW < b.CLOSE_OF_ESCROW ? -1 : a.CLOSE_OF_ESCROW > b.CLOSE_OF_ESCROW ? 1 : 0;
                })
                : sort(deduped);

            if (!deduped.length) {
                this.setState({ isBusy: null, data: false, original: false });
                return;
            }

            const applyState = (leadStateMap) => {
                if (leadStateMap) {
                    deduped.forEach(row => {
                        const key = row.BUYER_LEAD_ID != null ? String(row.BUYER_LEAD_ID) : null;
                        if (key && leadStateMap[key]) {
                            row.BUYING_STATE = leadStateMap[key];
                        }
                    });
                    data.forEach(row => {
                        const key = row.BUYER_LEAD_ID != null ? String(row.BUYER_LEAD_ID) : null;
                        if (key && leadStateMap[key]) {
                            row.BUYING_STATE = leadStateMap[key];
                        }
                    });
                }
                this.setState({
                    isBusy: null,
                    data: convertToTableFormat({dateProps, checkboxProps, priceProps, percentProps, data, hasAgentData: !isHsc, hasBorrower: isHsc}),
                    original: cloneDeep(data)
                });
            };

            if (!isHsc) {
                const leadIds = [...new Set(deduped.map(r => r.BUYER_LEAD_ID).filter(Boolean))];
                if (leadIds.length) {
                    fetch(`/api/leads/by-ids?ids=${leadIds.join(',')}&columns=id,state`)
                        .then(r => r.ok ? r.json() : r.text().then(text => { console.error('[BuyingState] CRM error:', r.status, text.substring(0, 300)); return null; }))
                        .then(json => {
                            const leadStateMap = {};
                            if (json && Array.isArray(json.data)) {
                                // Use string keys to match Arcimedes string values
                                json.data.forEach(lead => { leadStateMap[String(lead.id)] = lead.state; });
                            } else {
                                console.warn('[BuyingState] Unexpected CRM response:', json);
                            }
                            applyState(leadStateMap);
                        })
                        .catch(err => { console.error('[BuyingState] fetch failed:', err); applyState(null); });
                } else {
                    console.warn('[BuyingState] No BUYER_LEAD_ID found in data. SQL re-upload may be needed.');
                    applyState(null);
                }
            } else {
                applyState(null);
            }
        });
    }

    handleCollapse(e, taskId, tasks) {
        const {collapsed, checkboxes} = this.state;
        let areAllCollapsed = this.state.areAllCollapsed;
        const checkboxKeys = Object.keys(checkboxes);
        let found = false;

        if (taskId) {
            checkboxes[taskId] = !checkboxes[taskId];
        } else {
            Object.keys(checkboxes).map(key => {
                checkboxes[key] = areAllCollapsed;
            });
            areAllCollapsed = !areAllCollapsed;
        }

        tasks.map(task => {
            if (taskId) {
                if (found) {
                    if (checkboxKeys.includes(task.id)) {
                        found = false;
                    }
                    if (task.canCollapse) {
                        collapsed[task.id] = !collapsed[task.id];
                    }
                }
                if (task.id === taskId) {
                    found = true;
                }
            } else {
                if (task.canCollapse) {
                    collapsed[task.id] = areAllCollapsed;
                }
            }
        });
        this.setState({collapsed, checkboxes, areAllCollapsed});
    }

    updateWindowDimensions() {
        this.setState({width: window.innerWidth - OVERFLOW_TABLE_WIDTH});
    }

    handleSave({e, dateProps, checkboxProps, priceProps, percentProps, excludedProps, tableCode, tasks}) {
        e.preventDefault();
        this.saveData({dateProps, checkboxProps, priceProps, percentProps, excludedProps, tableCode, tasks});
    }

    saveData({dateProps, checkboxProps, priceProps, percentProps, excludedProps, tableCode, tasks}) {
        if (this.state.isBusy) return;

        this.setState({isBusy: true});

        let data = convertToBackendFormat({dateProps, checkboxProps, data: this.state.data});

        // Discard unchanged values and erase additional data
        data.map((row, index) => {
            const id = row.ARCIMEDES_ROW_ID;
            Object.keys(row).forEach(col => {
                if (excludedProps && excludedProps.includes(col) || row[col] === this.state.original[index][col]) {
                    delete row[col];
                }
            });
            if (Object.keys(row).length) {
                row.ARCIMEDES_ROW_ID = id;
            }
        });

        data = data.filter(row => Object.keys(row).length);

        // No changes detected, nothing to do
        if (!data.length) {
            this.setState({
                isBusy: null,
                error: null,
                success: 'No data to update',
            });
            return;
        } else {
            this.setState({
                isBusy: true,
                error: null,
                success: null,
            });
        }

        return request(arc.path.api, {
            service: 'arcimedes',
            action: 'open.datasource.table.Data.updateByTableCode',
            params: [tableCode, {
                value: data
            }],
        }).then(res => {
            if (res && res.success) {
                if (tableCode === SUMMIT_ESCROW_TRACKING) {
                    this.saveEffectiveDate(data, this.state.original, tasks);
                    const escrowPropertyId =
                        this.state.original &&
                        this.state.original[0] &&
                        this.state.original[0].PROPERTY_ID != null
                            ? String(this.state.original[0].PROPERTY_ID)
                            : (this.props.location.state ||
                                (this.props.location.pathname && this.props.location.pathname.split('/')[2]));
                    if (escrowPropertyId) {
                        syncPropertyEmailNotifications(escrowPropertyId).catch(function (err) {
                            console.error('syncPropertyEmailNotifications', err && err.message ? err.message : err);
                        });
                    }
                }
                const propertyId = this.props.location.state;
                const isHsc = tableCode === HSC_TRACKING;
                this.fetch({propertyId, dateProps, checkboxProps, priceProps, percentProps, saved: true, isHsc});
            } else {
                this.setState({
                    isBusy: null,
                    error: res && res.message ? res.message : 'Unspecified error',
                });
            }
        });
    }

    saveEffectiveDate(data, original, tasks) {
        const tasksToSave = tasks.filter(task => task.saveToProperty);
        const propertiesToSave = [];
        let index = 0;
        data.map((row, dataIndex) => {
            tasksToSave.map(task => {
                if (row[task.id]) {
                    if (!propertiesToSave[index])
                        propertiesToSave[index] = {
                            ARCIMEDES_ROW_ID: original[dataIndex].PROPERTY_ID,
                        };
                    propertiesToSave[index][task.id] = row[task.id];
                    index++;
                }
            });
        });

        if (propertiesToSave.length)
            request(arc.path.api, {
                service: 'arcimedes',
                action: 'open.datasource.table.Data.updateByTableCode',
                params: [PROPERTIES, {
                    value: propertiesToSave
                }],
            });
    }

    handleChange(e) {
        const name = e.currentTarget.name.split('-');
        const row = name[0];
        const col = name[1];
        this.setData(row, col, e.currentTarget.value);
    }

    handleChangeDropdown(query, queryType, x, key, key2, propertyId) {
        this.setData(propertyId, key, query[key], key2, query[key2]);
    }

    setData(row, col, value, col2, value2) {
        const data = this.state.data;
        const rowIndex = findIndex(data, (obj) => {return obj.ARCIMEDES_ROW_ID == row});
        if (row && col && !col.includes('_CHECKBOX')) {
            data[rowIndex][col] = value;
            if (col2) data[rowIndex][col2] = value2;
        }

        if (col === 'EFFECTIVE_DATE' || col === 'FEASIBILITY_PERIOD' || col === 'FEASIBILITY_EXTENSION') {
            const effectiveDate = moment(data[rowIndex].EFFECTIVE_DATE);
            const period = parseInt(data[rowIndex].FEASIBILITY_PERIOD);
            const extension = data[rowIndex].FEASIBILITY_EXTENSION ? parseInt(data[rowIndex].FEASIBILITY_EXTENSION) : 0;
            const feasibilityDate = calculateDate(effectiveDate, period, extension);
            if (effectiveDate && period) {
                data[rowIndex].FEASIBILITY_PERIOD_EXPIRES__DEPOSIT_NONREFUNDABLE = feasibilityDate;
            } else {
                data[rowIndex].FEASIBILITY_PERIOD_EXPIRES__DEPOSIT_NONREFUNDABLE = '';
            }
        }

        if (col === 'EFFECTIVE_DATE' || col === 'CLOSING_PERIOD' || col === 'CLOSING_EXTENSION') {
            const effectiveDate = moment(data[rowIndex].EFFECTIVE_DATE);
            const period = parseInt(data[rowIndex].CLOSING_PERIOD);
            const extension = data[rowIndex].CLOSING_EXTENSION ? parseInt(data[rowIndex].CLOSING_EXTENSION) : 0;
            const closingDate = calculateDate(effectiveDate, period, extension);
            if (effectiveDate && period) {
                data[rowIndex].CLOSING_DATE = closingDate;
            } else {
                data[rowIndex].CLOSING_DATE = '';
            }
        }

        if (col.includes('_CHECKBOX'))
            data[rowIndex][col] = !data[rowIndex][col];

        this.setState({data});
    }

    addNewItem(addNewItemKey, addNewItemType, index) {
        this.setState({addNewItemKey, addNewItemType, index}, this.toggleDrawer);
    }

    toggleDrawer() {
        this.setState((prevState) => ({showDrawer: !prevState.showDrawer}));
    }

    closeDrawer() {
        this.setState({showDrawer: false});
    }

    savedNewItem(newData) {
        // select the new item id & close drawer
        const key = this.state.addNewItemKey;
        const type = this.state.addNewItemType;
        const index = this.state.index;
        const state = {
            data: cloneDeep(this.state.data),
        };

        state.data[index][key] = newData.ARCIMEDES_ROW_ID;
        state.data[index]['BORROWER_TYPE'] = type;

        switch (type) {
            case 'buyer':
                state.newBuyer = {...newData, groupedBy: type};
                break;
            case "seller":
                state.newSeller = {...newData, groupedBy: type};
                break;
        }
        this.setState(state, () => this.toggleDrawer());
    }

    getDrawerComponent() {
        const component = this.state.addNewItemType;

        switch (component) {
            case 'buyer':
                return (
                    <BuyerDrawer
                        savedNewItem={this.savedNewItem}
                        closeDrawer={this.closeDrawer}
                    />
                );
            case 'seller':
                return (
                    <SellerDrawer
                        savedNewItem={this.savedNewItem}
                        closeDrawer={this.closeDrawer}
                    />
                );
        }
    }

    renderHeader({date, tasks, dateProps, checkboxProps, priceProps, percentProps, excludedProps, tableCode, areAllCollapsed = null}) {
        return (
            <React.Fragment>
                <h3 className='Tracking_h3'>{this.state.title}</h3>
                <h4 className='Tracking_h4'>{date}</h4>
                {(this.editsEveryRow() || this.editsOwnRows()) && <button className='Tracking_save' data-busy={this.state.isBusy} onClick={(e) => this.handleSave({e, dateProps, checkboxProps, priceProps, percentProps, excludedProps, tableCode, tasks})}>Save</button>}
                { areAllCollapsed !== null ? <button className='Tracking_save' data-busy={this.state.isBusy} onClick={(e) => this.handleCollapse(e, null, tasks)}>{areAllCollapsed ? 'Expand' : 'Collapse'} Rows</button> : null }
                { this.state.success ? <Alert type='success'>{this.state.success}</Alert> : null }
                { this.state.error ? <Alert type='error'>{this.state.error}</Alert> : null }
            </React.Fragment>
        )
    }

    getStaticTrackingLink(taskId, element) {
        switch (taskId) {
            case 'BORROWER_NAME':
                if (element.LOAN_TYPE === 'Purchase' && element.BUYER_LEAD_ID) {
                    return `https://accounts.kore.co/crm/lead/${element.BUYER_LEAD_ID}`;
                }
                if ((element.LOAN_TYPE === 'Construction' || element.LOAN_TYPE === 'Refi') && element.INVESTOR_LEAD_ID) {
                    return `https://accounts.kore.co/crm/lead/${element.INVESTOR_LEAD_ID}`;
                }
                if (element.BUYER_LEAD_ID) return `https://accounts.kore.co/crm/lead/${element.BUYER_LEAD_ID}`;
                if (element.INVESTOR_LEAD_ID) return `https://accounts.kore.co/crm/lead/${element.INVESTOR_LEAD_ID}`;
                return null;
            case 'BUYER_NAME':
                return element.BUYER_LEAD_ID ? `https://accounts.kore.co/crm/lead/${element.BUYER_LEAD_ID}` : null;
            case 'SELLER_NAME':
                return element.INVESTOR_LEAD_ID ? `https://accounts.kore.co/crm/lead/${element.INVESTOR_LEAD_ID}` : null;
            case 'BUYING_AGENT_NAME':
                return element.BUYER_AGENT_4_LEAD_ID ? `https://accounts.kore.co/crm/lead/${element.BUYER_AGENT_4_LEAD_ID}` : null;
            case 'LISTING_AGENT_NAME':
                return element.LISTING_AGENT_4_LEAD_ID ? `https://accounts.kore.co/crm/lead/${element.LISTING_AGENT_4_LEAD_ID}` : null;
            case 'ESCROW_OFFICER':
                return element.ESCROW_OFFICER_LEAD_ID ? `https://accounts.kore.co/crm/lead/${element.ESCROW_OFFICER_LEAD_ID}` : null;
            default:
                return null;
        }
    }

    renderTableElement(task, element, isGray, editable) {
        switch (task.type) {
            case 'date':
                return (
                    <div className="Tracking_date_wrapper">
                        <Input
                            theme={task.highlighted ? 'highlighted_without_border' : (isGray ? 'gray_without_border' : 'without_border')}
                            className="Tracking_td" type='text' name={`${element.ARCIMEDES_ROW_ID}-${task.id}`}
                            value={element[task.id]} onChange={this.handleChange} placeholder='MM/DD/YYYY'
                            title='MM/DD/YYYY'
                            pattern='(0?[1-9]|1[012])[\/](0?[1-9]|[12][0-9]|3[01])[\/]([1|2]\d\d\d)'
                            disabled={!editable}/>
                        {
                            element[task.id + '_CHECKBOX'] ?
                                <input name={`${element.ARCIMEDES_ROW_ID}-${task.id}_CHECKBOX`}
                                       className="Tracking_date_checkbox" type="checkbox" checked
                                       onChange={this.handleChange} disabled={!editable}/>
                                :
                                <input name={`${element.ARCIMEDES_ROW_ID}-${task.id}_CHECKBOX`}
                                       className="Tracking_date_checkbox" type="checkbox" onChange={this.handleChange} disabled={!editable}/>
                        }
                    </div>
                );
            case 'dropdown':
                return <Select theme={isGray ? 'gray_without_border' : 'without_border'}
                               name={`${element.ARCIMEDES_ROW_ID}-${task.id}`} value={element[task.id]}
                               onChange={this.handleChange} placeholder='Yes/No' disabled={!editable}>
                    <option value=""/>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                </Select>;
            case 'text':
                return <Input theme={isGray ? 'gray_without_border' : 'without_border'} className="Tracking_td"
                              type='text' name={`${element.ARCIMEDES_ROW_ID}-${task.id}`} value={element[task.id]}
                              onChange={this.handleChange} disabled={!editable}/>;
            case 'vendorSelect': {
                const companyKey = task.companyKey || 'TITLE_COMPANY';
                const leadIdKey = task.leadIdKey || 'ESCROW_OFFICER_LEAD_ID';
                return (
                    <SelectEscrowOfficer
                        value={element[task.id]}
                        vendorType={task.vendorType}
                        contactNoun={task.contactNoun}
                        storedLeadId={element[leadIdKey] || null}
                        companyFilter={element[companyKey] || null}
                        onSelect={(name, companyName, leadId) => {
                            const rowIndex = this.state.data.findIndex(r => r.ARCIMEDES_ROW_ID == element.ARCIMEDES_ROW_ID);
                            if (rowIndex !== -1) this.state.data[rowIndex][leadIdKey] = leadId || null;
                            this.setData(element.ARCIMEDES_ROW_ID, task.id, name, companyKey, companyName);
                        }}
                        disabled={!editable}
                    />
                );
            }
            case 'titleCompanySelect':
                // Title Company is auto-filled from the selected Escrow Officer; render as read-only text.
                return <div>{element[task.id] || ''}</div>;
            case 'readOnlyText':
                // Auto-filled from a paired vendorSelect task (e.g. Lender <- Lender Contact).
                return <div>{element[task.id] || ''}</div>;
            case 'static': {
                const staticLink = this.getStaticTrackingLink(task.id, element);
                if (staticLink && element[task.id]) {
                    return <a href={staticLink} target="_blank">{element[task.id]}</a>;
                }
                return <div>{element[task.id]}</div>;
            }
        }
    }

    /** Whether the member may edit every row of this board. Each board decides from its own permission. */
    editsEveryRow() {
        return false;
    }

    /** Whether the member may edit the rows of the deals they are listing or buyer agent 1-3 on. */
    editsOwnRows() {
        return true;
    }

    renderTable({data, tasks, checkboxes, checkboxKeys, collapsed, isOneProperty = false}) {
        const editsEveryRow = this.editsEveryRow();
        const editsOwnRows = this.editsOwnRows();
        if (data === false) return <div className='Tracking_no_data'>No data found</div>;
        return (
            <div style={{width: isOneProperty ? `${width}%` : this.state.width}}
                 className={(this.state.success || this.state.error) ? 'Tracking_scroll_container Tracking_smaller_height' : 'Tracking_scroll_container'}>
                <table className="Tracking_table">
                    <thead>
                    <tr>
                        <th className="Tracking_th with_padding Tracking_border_top Tracking_border_left"
                            id="Tracking_task_cell">
                            Task {data.length > 1 ? `- ${data.length} Properties in Escrow` : ''}
                        </th>
                        {
                            data.map(row => {
                                let name, city;
                                if (row.PROPERTY_NAME) {
                                    name = row.PROPERTY_NAME;
                                    city = [row.PROPERTY_CITY, row.PROPERTY_STATE].filter(Boolean).join(', ');
                                } else {
                                    const index = row.TASK ? row.TASK.lastIndexOf('-') : -1;
                                    name = row.TASK && index > -1 ? row.TASK.slice(0, index - 1) : 'N/A';
                                    city = row.TASK && index > -1 ? row.TASK.slice(index + 2) : 'N/A';
                                }
                                return (
                                    <th key={row.ARCIMEDES_ROW_ID}
                                        className="Tracking_th Tracking_center Tracking_border_top">
                                        <Link to={{pathname: '/property/' + row.PROPERTY_ID}}>
                                            {name}<br/>{city}
                                        </Link>
                                    </th>
                                );
                            })
                        }
                    </tr>
                    </thead>
                    <tbody>
                    {
                        tasks.map((task, taskIndex) => {
                            return (
                                <tr key={task.id}>
                                    <th className={`Tracking_th ${task.isSticky ? 'sticky' : ''} ${task.isSubTask ? 'subtask' : 'with_padding'} Tracking_border_left${task.highlighted ? ' Tracking_highlighted' : ''} ${collapsed && collapsed[task.id] ? 'Tracking_hidden_row' : 'Tracking_visible_row'}`}
                                        style={{top: task.isSticky ? (this.state.width > limitWidth ? (headerCellHeight + taskIndex * cellHeight) : (headerCellHeightSmall + taskIndex * cellHeightSmall)) : 0}}>
                                        {task.step && `${task.step}. `}{task.name}
                                        {checkboxKeys.includes(task.id) ?
                                            <div className={'Tracking_eye'}
                                                 dangerouslySetInnerHTML={{__html: (checkboxes[task.id] ? arrowDown : arrowRight)}}
                                                 onClick={(e) => this.handleCollapse(e, task.id, tasks)}/>
                                            : ''
                                        }
                                    </th>
                                    {
                                        data ? data.map((element, index) => {
                                            let editable;
                                            if (!editsEveryRow && !editsOwnRows) {
                                                editable = false;
                                            } else if (!editsEveryRow) {
                                                // Case and stray spaces differ between the deal's
                                                // agent emails and the login, so compare normalised.
                                                const normalizeEmail = (email) => String(email == null ? '' : email).trim().toLowerCase();
                                                const userEmail = normalizeEmail(this.props.userData.email);
                                                const agentsEmails = [
                                                    element.LISTING_AGENT_1_EMAIL,
                                                    element.LISTING_AGENT_2_EMAIL,
                                                    element.LISTING_AGENT_3_EMAIL,
                                                    element.BUYER_AGENT_1_EMAIL,
                                                    element.BUYER_AGENT_2_EMAIL,
                                                    element.BUYER_AGENT_3_EMAIL
                                                ].map(normalizeEmail);
                                                editable = userEmail !== '' && agentsEmails.includes(userEmail);
                                            } else {
                                                editable = true;
                                            }
                                            if (task.lockedByKey && element[task.lockedByKey]) {
                                                editable = false;
                                            }

                                            const isGray = index % 2;
                                            const isWidePickCell =
                                                task.type === 'vendorSelect' || task.type === 'titleCompanySelect' || task.type === 'readOnlyText';
                                            return (
                                                <td key={element.ARCIMEDES_ROW_ID}
                                                    style={{
                                                        top: task.isSticky ? (this.state.width > limitWidth ? (headerCellHeight + taskIndex * cellHeight) : (headerCellHeightSmall + taskIndex * cellHeightSmall)) : 0,
                                                        minWidth: isWidePickCell ? 248 : undefined
                                                    }}
                                                    className={`Tracking_td ${task.isSticky ? 'sticky' : ''}${task.highlighted ? ' Tracking_highlighted' : isGray ? ' Tracking_gray' : ''}${collapsed && collapsed[task.id] ? ' Tracking_hidden_row' : ' Tracking_visible_row'}`}>
                                                    {this.renderTableElement(task, element, isGray, editable)}
                                                </td>
                                            );
                                        }) : ''
                                    }
                                </tr>
                            );
                        })
                    }
                    </tbody>
                </table>
            </div>
        )
    }
}